// src/hooks/useAccueil.ts — SCE Issues
// Ce que l'accueil V2 (Lot 9) charge EN PLUS de la liste des problèmes :
//
//  - les derniers événements (fil « Activité récente ») ;
//  - la date de tous les événements, pour « Sans activité depuis 7 j » —
//    seulement pour un coordinateur, les autres n'ont pas ce compteur ;
//  - la visite précédente de l'accueil (« Nouveau depuis votre dernière
//    visite »), relue puis remplacée par maintenant.
//
// La visite précédente est lue UNE fois par affichage de la page : le bouton
// Rafraîchir recharge l'activité, mais ne déplace pas la coupure « Nouveau /
// Déjà vu » — sinon le nouveau disparaîtrait au premier clic.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getActiviteRecente,
  listerDatesEvenements,
  type DateEvenement,
} from '@/api/commentaires.api'
import { passerSurAccueil, type PassageAccueil } from '@/api/userPrefs.api'
import type { BlocRetourAbsence } from '@/utils/retourAbsence'
import type { Commentaire } from '@/types/issue.types'
import { useCurrentUser } from './useCurrentUser'
import { useSurProblemesModifies } from './signalProblemesModifies'
import { poserRetour } from '@/features/retour-absence/etat'

export interface DonneesAccueil {
  evenements: Commentaire[]
  /** null tant que non chargé, ou si ce profil n'en a pas besoin. */
  dates: DateEvenement[] | null
  /** Visite précédente (ISO) ; null au premier passage. */
  visitePrecedente: string | null
  /** La visite précédente est connue (lue, ou échec de lecture). */
  visiteLue: boolean
  /** Retour d'absence en cours, tel que lu à ce passage (28/10) ; le bloc vivant est dans features/retour-absence. */
  retour: BlocRetourAbsence | null
  loading: boolean
  error: string | null
}

// Deux montages rapprochés (StrictMode en développement, aller-retour éclair)
// partagent la même lecture : le second relirait sinon la visite que le
// premier vient d'écrire, et plus rien ne serait « nouveau ».
let lectureRecente: { upn: string; t: number; promesse: Promise<PassageAccueil> } | null = null

function visiteAccueil(upn: string, ecrire: boolean, avecRetour: boolean): Promise<PassageAccueil> {
  const t = Date.now()
  if (lectureRecente && lectureRecente.upn === upn && t - lectureRecente.t < 5000) {
    return lectureRecente.promesse
  }
  const promesse = passerSurAccueil(upn, new Date(t).toISOString(), ecrire, avecRetour)
  lectureRecente = { upn, t, promesse }
  return promesse
}

export function useAccueil(avecDates: boolean): DonneesAccueil {
  const { user, enVueRole, isFournisseur } = useCurrentUser()
  const upn = user?.upn ?? ''

  const [evenements, setEvenements] = useState<Commentaire[]>([])
  const [dates, setDates] = useState<DateEvenement[] | null>(null)
  const [visitePrecedente, setVisitePrecedente] = useState<string | null>(null)
  const [visiteLue, setVisiteLue] = useState(false)
  const [retour, setRetour] = useState<BlocRetourAbsence | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Seule la dernière requête lancée compte (Rafraîchir pendant un chargement).
  const generation = useRef(0)

  const charger = useCallback(async (): Promise<boolean> => {
    const g = ++generation.current
    try {
      const [evts, ds] = await Promise.all([
        getActiviteRecente(),
        avecDates ? listerDatesEvenements() : Promise.resolve(null),
      ])
      if (g !== generation.current) return true
      setEvenements(evts)
      setDates(ds)
      setError(null)
      return true
    } catch (err: unknown) {
      if (g === generation.current) {
        setError(err instanceof Error ? err.message : 'Activité indisponible.')
      }
      return false
    } finally {
      if (g === generation.current) setLoading(false)
    }
  }, [avecDates])

  useEffect(() => {
    void charger()
  }, [charger])

  useSurProblemesModifies(charger)

  // Visite précédente : une fois par compte affiché. En bascule de rôle, on lit
  // la ligne de la personne affichée sans jamais y écrire (règle du Lot 7).
  useEffect(() => {
    if (!upn) return
    let annule = false
    setVisiteLue(false)
    visiteAccueil(upn, !enVueRole, !isFournisseur)
      .then((v) => {
        if (annule) return
        setVisitePrecedente(v.precedente)
        setRetour(v.retour)
        poserRetour(upn, v.retour)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[accueil] dernière visite indisponible', err)
      })
      .finally(() => {
        if (!annule) setVisiteLue(true)
      })
    return () => {
      annule = true
    }
  }, [upn, enVueRole, isFournisseur])

  return { evenements, dates, visitePrecedente, visiteLue, retour, loading, error }
}
